import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Brain, Sparkles, Ear, BookOpen,
  Lightbulb, FileText, Phone, Inbox, UserCircle,
  Check, CalendarCheck,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";

/* ============ DATA ============ */

const PILLARS = [
  { letter: "K", title: "Knowledge",   desc: "Understanding what happened." },
  { letter: "A", title: "Assistance",  desc: "Helping your team accomplish more." },
  { letter: "I", title: "Intelligence",desc: "Insights that drive better decisions." },
];

const FRAGMENTS = [
  "Phone calls.", "Text messages.", "Emails.",
  "Appointments.", "Patient inquiries.", "New leads.", "Missed opportunities.",
];

const KAI_NEVER = [
  "KAI never misses a call.",
  "KAI never forgets a conversation.",
  "KAI never overlooks a follow-up.",
  "KAI never loses context.",
];

const CAPABILITIES = [
  {
    id: "listens",
    Icon: Ear,
    headline: "KAI Listens",
    promise: "Every conversation contains valuable information.",
    body: "KAI automatically listens to interactions across your CRM and communication channels.",
    bullets: [
      "AI call handling",
      "Call recording analysis",
      "Voice transcription",
      "Inbox intelligence",
      "Communication monitoring",
      "Multi-channel conversation tracking",
    ],
  },
  {
    id: "understands",
    Icon: Brain,
    headline: "KAI Understands",
    promise: "A conversation is more than words.",
    body: "KAI identifies the meaning behind every interaction — so your team gets immediate insight without rewinding the tape.",
    bullets: [
      "Caller intent",
      "New patient requests",
      "Existing patient requests",
      "Appointment changes",
      "Cancellation risks",
      "Lead opportunities",
      "Customer sentiment",
      "Reactivation opportunities",
      "Service interests",
    ],
  },
  {
    id: "remembers",
    Icon: BookOpen,
    headline: "KAI Remembers",
    promise: "Every interaction becomes part of a living patient story.",
    body: "AI Snapshots assemble communication history, appointment activity and conversation insights into one place.",
    bullets: [
      "Communication history",
      "Appointment activity",
      "Lead journey",
      "Patient interests",
      "Previous conversations",
      "Follow-up status",
      "Key insights",
    ],
  },
  {
    id: "summarizes",
    Icon: FileText,
    headline: "KAI Summarizes",
    promise: "Nobody wants to listen to a 15-minute recording to find one detail.",
    body: "KAI automatically distills every conversation into the parts that matter most.",
    bullets: [
      "Call summaries",
      "Conversation summaries",
      "Follow-up notes",
      "Action items",
      "Patient insights",
      "Team handoff notes",
    ],
  },
  {
    id: "guides",
    Icon: Lightbulb,
    headline: "KAI Guides",
    promise: "Knowing what happened is helpful. Knowing what to do next is powerful.",
    body: "KAI continuously surfaces opportunities and recommends the next best action.",
    bullets: [
      "Patient hasn't booked again in 90 days",
      "Follow-up is overdue",
      "Lead has not been contacted",
      "Patient expressed interest in a service",
      "Caller requested a callback",
      "Reactivation opportunity detected",
    ],
  },
];

const PLATFORM_LIVE = [
  {
    Icon: Sparkles,
    name: "Leads",
    bullets: ["Lead intelligence", "Intent detection", "Opportunity insights", "Recommended actions"],
  },
  {
    Icon: Inbox,
    name: "Inbox",
    bullets: ["Conversation summaries", "AI notes", "Interaction insights", "Quick context"],
  },
  {
    Icon: Phone,
    name: "Call Tracker",
    bullets: ["Call intelligence", "AI call summaries", "Intent classification", "Outcome analysis", "Team performance insights"],
  },
  {
    Icon: UserCircle,
    name: "Patient Profile",
    bullets: ["AI patient snapshot", "Relationship timeline", "Reactivation opportunities", "Patient journey intelligence"],
  },
];

const CONTRASTS = [
  { mostly: "records activity", kai: "understands activity" },
  { mostly: "stores information", kai: "creates intelligence" },
  { mostly: "tells you what happened", kai: "helps you decide what happens next" },
];

const CLOSING_STATEMENTS = [
  "KAI Listens.",
  "KAI Understands.",
  "KAI Remembers.",
  "KAI Assists.",
  "KAI Helps You Take Action.",
];

/* ============ PAGE ============ */

export default function Kai() {
  const openDemo = useDemo();

  return (
    <article data-testid="kai-page" data-snap className="text-foreground">
      {/* ───── HERO ───── */}
      <section
        id="kai-hero"
        className="relative overflow-hidden pt-16 pb-10 sm:pt-32 sm:pb-28"
      >
        {/* Deep ocean ambient */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[600px]">
          <div className="absolute inset-x-0 top-0 h-full bg-[radial-gradient(60%_60%_at_50%_0%,rgba(6,182,212,0.22),transparent_70%)]" />
          <div className="absolute inset-x-0 top-0 h-full bg-[radial-gradient(40%_40%_at_80%_30%,rgba(20,184,166,0.18),transparent_70%)]" />
        </div>

        <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-12">
          <div className="text-center">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-500" />
              </span>
              The brain inside Clinic OS
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-0 sm:mt-8 font-display text-[5rem] font-black leading-[0.95] tracking-[-0.04em] sm:text-[7rem] lg:text-[9rem]"
            >
              <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
                KAI.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-3 sm:mt-6 font-display text-lg font-bold tracking-tight text-foreground/90 sm:text-3xl lg:text-4xl"
            >
              Knowledge. Assistance. Intelligence.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.22 }}
              className="mx-auto mt-3 sm:mt-8 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg"
            >
              Your AI companion inside Clinic OS. A trusted teammate that works quietly
              alongside your team — transforming conversations into knowledge, knowledge
              into action, and action into better patient experiences.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 sm:mt-10 flex flex-col items-stretch sm:items-center justify-center gap-2.5 sm:gap-3 sm:flex-row sm:flex-wrap"
            >
              <Link
                to="/kai-experience"
                data-testid="kai-hero-try"
                className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-600 px-7 text-sm font-bold text-white shadow-lg shadow-fuchsia-500/30 ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
              >
                <Sparkles className="h-4 w-4" />
                Try KAI live
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <button
                onClick={openDemo}
                data-testid="kai-hero-demo"
                className="hidden sm:inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-cyan-600 via-teal-600 to-emerald-600 px-7 text-sm font-semibold text-white shadow-lg shadow-teal-500/20 transition-all hover:-translate-y-0.5"
              >
                <CalendarCheck className="h-4 w-4" /> Book a Demo
              </button>
              <a
                href="#capabilities"
                data-testid="kai-hero-explore"
                className="hidden sm:inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-border bg-card px-6 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
              >
                See what KAI does <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* (PainPoints "Sound familiar?" and DiscChapters "KAI, four ways" removed per request) */}

      {/* ───── WHY WE BUILT KAI ───── */}
      <section className="py-20 sm:py-28" data-testid="kai-why">
        <div className="mx-auto max-w-4xl px-6 sm:px-8 lg:px-12">
          <p className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
            Why we built KAI
          </p>
          <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] sm:text-5xl lg:text-6xl">
            Every business has{" "}
            <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
              conversations.
            </span>
          </h2>

          <div className="mt-10 flex flex-wrap gap-3">
            {FRAGMENTS.map((f, i) => (
              <motion.span
                key={f}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="rounded-full border border-border bg-card px-4 py-1.5 font-display text-base font-semibold tracking-tight text-foreground/80 sm:text-lg"
              >
                {f}
              </motion.span>
            ))}
          </div>

          <div className="mt-12 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            <p>
              The challenge has never been collecting information.{" "}
              <span className="font-semibold text-foreground">
                The challenge has always been understanding it.
              </span>
            </p>
            <p>
              Every day, valuable insights are hidden inside conversations. Staff are
              busy. Notes are incomplete. Follow-ups are forgotten. Opportunities are
              missed.
            </p>
            <p className="font-display text-xl font-bold text-foreground sm:text-2xl">
              That&apos;s why we built KAI.
            </p>
          </div>
        </div>
      </section>

      {/* ───── WHY THE NAME ───── */}
      <section className="section-bases py-20 sm:py-28" data-testid="kai-name">
        <div className="mx-auto max-w-5xl px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
              Why the name KAI?
            </p>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] sm:text-5xl lg:text-6xl">
              Three letters.{" "}
              <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
                One promise.
              </span>
            </h2>
          </div>

          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.letter}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`kai-letter-${p.letter.toLowerCase()}`}
                className="relative overflow-hidden rounded-3xl border border-border bg-card p-8"
              >
                <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-teal-500/15 blur-2xl" />
                <span
                  className="font-display text-[6rem] font-black leading-none tracking-[-0.05em] text-transparent sm:text-[7rem]"
                  style={{ WebkitTextStroke: "1.5px rgba(20,184,166,0.5)" }}
                  aria-hidden="true"
                >
                  {p.letter}
                </span>
                <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {p.desc}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Ocean / 海 story */}
          <div className="mt-16 rounded-3xl border border-border bg-card p-8 sm:p-12">
            <div className="grid items-center gap-8 lg:grid-cols-[auto_1fr] lg:gap-14">
              <div className="text-center lg:text-left">
                <span className="font-display text-[10rem] font-black leading-[0.85] tracking-[-0.05em] text-transparent bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text sm:text-[12rem]">
                  海
                </span>
                <p className="mt-2 font-mono text-xs text-muted-foreground">
                  Kai · Japanese · ocean
                </p>
              </div>
              <div>
                <p className="font-display text-xl leading-snug tracking-tight text-foreground sm:text-2xl">
                  An ocean is vast. Connected. Deep.
                </p>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  Just like the thousands of interactions happening throughout your
                  business every day. Every call. Every message. Every appointment.
                  Every patient interaction.
                </p>
                <p className="mt-4 font-semibold text-foreground">
                  KAI brings them together into one connected ocean of intelligence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── THE TEAMMATE THAT NEVER SLEEPS ───── */}
      <section className="py-20 sm:py-28" data-testid="kai-never-sleeps">
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-8 lg:px-12">
          <p className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-500" />
            </span>
            Always on
          </p>
          <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] sm:text-5xl lg:text-6xl">
            The teammate that{" "}
            <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
              never sleeps.
            </span>
          </h2>
          <div className="mx-auto mt-12 max-w-2xl space-y-3 text-center font-display text-2xl font-bold tracking-tight text-foreground/85 sm:text-3xl">
            {KAI_NEVER.map((line, i) => (
              <motion.p
                key={line}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
              >
                {line}
              </motion.p>
            ))}
          </div>
          <p className="mx-auto mt-10 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            While your team focuses on caring for patients, KAI works behind the scenes
            so every interaction is understood.
          </p>
        </div>
      </section>

      {/* ───── WHAT KAI DOES — 5 CAPABILITIES ───── */}
      <section
        id="capabilities"
        className="section-bases py-20 sm:py-28"
        data-testid="kai-capabilities"
      >
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
              What KAI does
            </p>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] sm:text-5xl lg:text-6xl">
              Five quiet superpowers,{" "}
              <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
                always on.
              </span>
            </h2>
          </div>

          <div className="mt-16 space-y-6">
            {CAPABILITIES.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`kai-capability-${c.id}`}
                className="grid gap-8 rounded-3xl border border-border bg-card p-7 sm:p-10 lg:grid-cols-12 lg:gap-12"
              >
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 text-white shadow-sm">
                      <c.Icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
                      {c.headline}
                    </h3>
                  </div>
                  <p className="mt-5 font-display text-lg font-semibold tracking-tight text-foreground/85 sm:text-xl">
                    {c.promise}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {c.body}
                  </p>
                </div>

                <div className="lg:col-span-7">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Capabilities
                  </p>
                  <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {c.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2.5 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm"
                      >
                        <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-teal-500" strokeWidth={3} />
                        <span className="text-foreground/90">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── KAI ACROSS THE PLATFORM ───── */}
      <section className="py-20 sm:py-28" data-testid="kai-platform">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
              KAI across the platform
            </p>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] sm:text-5xl lg:text-6xl">
              Embedded everywhere{" "}
              <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
                your team works.
              </span>
            </h2>
          </div>

          {/* Available today */}
          <div className="mt-14">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                Live across Clinic OS
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
              {PLATFORM_LIVE.map((m) => (
                <div
                  key={m.name}
                  className="rounded-3xl border border-border bg-card p-7"
                  data-testid={`kai-platform-live-${m.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 text-white">
                      <m.Icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-display text-xl font-bold tracking-tight">
                      {m.name}
                    </h3>
                  </div>
                  <ul className="mt-5 grid gap-1.5 sm:grid-cols-2">
                    {m.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-xs text-foreground/85"
                      >
                        <Check className="mt-0.5 h-3 w-3 flex-none text-teal-500" strokeWidth={3} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───── MORE THAN AI ───── */}
      <section className="section-bases py-20 sm:py-28" data-testid="kai-contrast">
        <div className="mx-auto max-w-5xl px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-600 dark:text-teal-400">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
              More than AI
            </p>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] sm:text-5xl lg:text-6xl">
              Software records.{" "}
              <span className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
                KAI understands.
              </span>
            </h2>
          </div>

          <div className="mt-14 space-y-4">
            {CONTRASTS.map((c, i) => (
              <motion.div
                key={c.kai}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                className="grid items-center gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-[1fr_auto_1fr] sm:gap-8"
              >
                <div className="text-sm text-muted-foreground sm:text-base">
                  <p className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground/70">
                    Most software
                  </p>
                  <p className="mt-1 line-through decoration-rose-400/60">
                    {c.mostly}
                  </p>
                </div>
                <div className="hidden h-12 w-px bg-border sm:block" />
                <div className="text-sm sm:text-base">
                  <p className="font-semibold uppercase tracking-wider text-[10px] text-teal-600 dark:text-teal-400">
                    KAI
                  </p>
                  <p className="mt-1 font-semibold text-foreground">{c.kai}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── WELCOME / CLOSING — dark cinematic CTA ───── */}
      <section
        data-testid="kai-closing"
        className="border-t border-border bg-gradient-to-b from-white via-slate-50/60 to-slate-100/60 py-16 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-20"
      >
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-[#0a0a14] px-8 py-16 text-center sm:px-16 sm:py-24">
            <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-teal-500/30 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-cyan-500/25 blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 bg-grid opacity-30" />
            <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-400" />
                </span>
                The future we&apos;re building
              </span>

              <h2 className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-6xl lg:text-7xl">
                Welcome to{" "}
                <span className="bg-gradient-to-br from-cyan-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
                  KAI.
                </span>
              </h2>

              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
                The AI companion behind every call. Every conversation. Every patient.
                Every lead. Every opportunity.
              </p>

              <div className="mx-auto mt-10 max-w-md space-y-2.5">
                {CLOSING_STATEMENTS.map((s, i) => (
                  <motion.p
                    key={s}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="font-display text-xl font-bold tracking-tight text-white/90 sm:text-2xl"
                  >
                    {s}
                  </motion.p>
                ))}
              </div>

              <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  onClick={openDemo}
                  data-testid="kai-final-demo"
                  className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 px-8 text-sm font-bold text-white shadow-lg shadow-teal-500/40 ring-1 ring-inset ring-white/10 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-teal-500/50 sm:w-auto"
                >
                  <CalendarCheck className="h-4 w-4" /> Book a Demo
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <Link
                  to="/build"
                  data-testid="kai-final-build"
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-8 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 sm:w-auto"
                >
                  Build Your OS
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <p className="mt-12 font-display text-[11px] font-bold uppercase tracking-[0.28em] text-teal-300">
                KAI · Knowledge • Assistance • Intelligence
              </p>
              <p className="mt-2 text-xs text-white/55">
                The AI Operating System Behind Clinic OS.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </article>
  );
}
